import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TipoConta } from '../../../../enums/TipoConta.enum';
import { Moeda } from '../../../../enums/moeda.enum';
import {
  ContaDTO,
  ContaFormDTO,
} from '../../../../models/gerenciar-contas/conta.model';
import { ContaService } from '../../../../services/conta/conta.service';
import { EmpresaConfigService } from '../../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../../models/configuracoes/empresa.model';
import { moedasCompativeis } from '../../../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-nova-conta',
  templateUrl: './nova-conta.component.html',
  styleUrl: './nova-conta.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class NovaContaComponent implements OnInit, OnDestroy {
  form!: ContaFormDTO;
  stateOptions: any[] = [];
  filterName = '';
  tipoConta: { label: string; value: string }[] = [];
  moedas: { label: string; value: string; flagUrl: string }[] = [];
  isEdit = false;
  idEdit = undefined;
  isFormSubmitted = false;
  submitting = false;

  // Empresa logada — usada para filtrar moedas pelo pareamento permitido
  empresa: EmpresaConfigDTO | null = null;

  // Textos traduzidos
  texts = {
    novaConta: '',
    editarConta: '',
    subtituloNova: '',
    subtituloEditar: '',
    dadosDaConta: '',
    valores: '',
    informacoesAdicionais: '',
  };

  labels = {
    tituloDaConta: '',
    numeroDaConta: '',
    tipoConta: '',
    moeda: '',
    saldo: '',
    descricao: '',
  };

  placeholders = {
    tituloDaConta: '',
    numeroDaConta: '',
    selecione: '',
    saldo: '',
    descricao: '',
  };

  botoes = {
    confirmar: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private contaService: ContaService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.buildTipoContaOptions();
    this.buildMoedaOptions();
    this.buildStateOptions();
    this.form = this.createEmptyForm();

    // Carrega empresa logada para filtrar moedas pelo pareamento permitido
    this.empresaService.getSessao().subscribe({
      next: (config) => {
        this.empresa = config;
        this.buildMoedaOptions(); // re-monta dropdown ja com filtro aplicado
      },
      error: () => {
        this.empresa = null;
        this.buildMoedaOptions();
      },
    });

    const idEdit = this.route.snapshot.paramMap.get('id');
    if (idEdit) {
      this.editConta(Number(idEdit));
    }

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
      this.buildTipoContaOptions();
      this.buildMoedaOptions();
      this.buildStateOptions();
    });
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations() {
    this.texts = {
      novaConta: this.translate.instant('nova-conta.texts.nova_conta'),
      editarConta: this.translate.instant('nova-conta.texts.editar_conta'),
      subtituloNova: this.translate.instant('nova-conta.texts.subtitulo_nova'),
      subtituloEditar: this.translate.instant('nova-conta.texts.subtitulo_editar'),
      dadosDaConta: this.translate.instant('nova-conta.texts.dados_da_conta'),
      valores: this.translate.instant('nova-conta.texts.valores'),
      informacoesAdicionais: this.translate.instant('nova-conta.texts.informacoes_adicionais'),
    };

    this.labels = {
      tituloDaConta: this.translate.instant('nova-conta.labels.titulo_da_conta'),
      numeroDaConta: this.translate.instant('nova-conta.labels.numero_da_conta'),
      tipoConta: this.translate.instant('nova-conta.labels.tipo_conta'),
      moeda: this.translate.instant('nova-conta.labels.moeda'),
      saldo: this.translate.instant('nova-conta.labels.saldo'),
      descricao: this.translate.instant('nova-conta.labels.descricao'),
    };

    this.placeholders = {
      tituloDaConta: this.translate.instant('nova-conta.placeholders.titulo_da_conta'),
      numeroDaConta: this.translate.instant('nova-conta.placeholders.numero_da_conta'),
      selecione: this.translate.instant('nova-conta.placeholders.selecione'),
      saldo: this.translate.instant('nova-conta.placeholders.saldo'),
      descricao: this.translate.instant('nova-conta.placeholders.descricao'),
    };

    this.botoes = {
      confirmar: this.translate.instant('nova-conta.botoes.confirmar'),
    };
  }

  private buildTipoContaOptions() {
    this.tipoConta = Object.values(TipoConta)
      .filter(tipo => tipo !== TipoConta.DINHEIRO)
      .map(tipo => ({
        label: this.translate.instant(`enums.tipo_conta.${tipo.toLowerCase()}`),
        value: String(tipo).toUpperCase(),
      }));
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Usado para filtrar o
   * dropdown de moedas no cadastro de conta.
   *
   * Fallback: enquanto a empresa nao carregou, retorna todas as 4
   * moedas para nao bloquear a UI. O filtro fica estavel apos a
   * primeira resposta de getSessao().
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }

  private buildMoedaOptions() {
    const permitidas = this.moedasPermitidas;
    this.moedas = Object.values(Moeda)
      .filter(moeda => permitidas.has(String(moeda).toUpperCase()))
      .map(moeda => ({
        label: this.translate.instant(`enums.moeda.${moeda.toLowerCase()}`),
        value: String(moeda).toUpperCase(),
        flagUrl: this.getFlagUrl(moeda),
      }));
  }

  getFlagUrl(moeda: any): string {
    const moedaStr = String(moeda || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  private buildStateOptions() {
    this.stateOptions = [
      { label: this.translate.instant('nova-conta.opcoes.ativo'), value: true },
      { label: this.translate.instant('nova-conta.opcoes.inativo'), value: false },
    ];
  }

  get selectedLocale(): string {
    if (!this.form.moeda) return 'es-PY';
    switch (this.form.moeda.value) {
      case 'REAL': return 'pt-BR';
      case 'DOLAR': return 'en-US';
      case 'GUARANI': return 'es-PY';
      default: return 'es-PY';
    }
  }

  get selectedCasasDecimais(): number {
    if (!this.form.moeda) return 0;
    return this.form.moeda.value === 'GUARANI' ? 0 : 2;
  }

  editConta(id: Number) {
  /*   if (id) {
      this.contaService.getById('/conta/' + id).subscribe((response) => {
        const tipoNota = this.tipoConta.find(
          (p) => p.value === (response.tipoConta as TipoConta)
        );

        const moeda = this.moedas.find(
          (p) => p.value === (response.moeda as Moeda)
        );

        this.form = {
          id: response.id,
          // codigoConta: response.codigoConta ?? null,
          descricao: response.descricao,
          moeda: moeda
            ? { label: String(moeda.label).toUpperCase(), value: moeda.value }
            : undefined,
          numeroConta: response.numeroConta,
          saldo: response.saldo,
          tipoConta: tipoNota
            ? {
                label: String(tipoNota.label).toUpperCase(),
                value: tipoNota.value,
              }
            : undefined,
          tituloDaConta: response.tituloDaConta,
          valorTotalDeEntradas: response.valorTotalDeEntradas,
          valorTotalDeSaidas: response.valorTotalDeSaidas,
        };
        this.isEdit = true;
      });
    } */
  }

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    const fields: (keyof ContaFormDTO)[] = [
      'descricao',
      'moeda',
      'saldo',
      'tipoConta',
      'tituloDaConta',
    ];

    let error = false;
    // Validacao generica dos campos
    for (const field of fields) {
      const fieldValue = this.form[field];

      // Verificar se o campo e obrigatorio e esta vazio, mas permitir 0 como valor valido
      if (
        fieldValue === null ||
        fieldValue === undefined ||
        String(fieldValue).trim() === ''
      ) {
        console.log('ERROR', field, fieldValue);
        const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1);
        const detail = this.translate.instant('nova-conta.validacoes.campo_obrigatorio', { field: fieldLabel });

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: detail,
        });
        error = true;
      }
    }
    if (error) {
      return;
    }

    const body: ContaDTO = {
      descricao: this.form.descricao!,
      moeda: this.form.moeda!.value as Moeda,
      numeroConta: this.form.numeroConta,
      saldo: this.form.saldo!,
      tipoConta: this.form.tipoConta!.value as TipoConta,
      tituloDaConta: this.form.tituloDaConta!,
      // Acumuladores da conta: quem os movimenta e o sistema, a cada entrada e saida. Conta nova
      // comeca zerada — nao ha nada para o usuario informar aqui.
      valorTotalDeEntradas: this.form.valorTotalDeEntradas ?? 0,
      valorTotalDeSaidas: this.form.valorTotalDeSaidas ?? 0,
      aceitaTarjetaQrcode: this.form.aceitaTarjetaQrcode,
      // Os dados bancarios da empresa nao sao mostrados ao cliente no Mini Shop.
      exibirParaTransferencia: false,
    };

    if (this.isEdit) {
      body.id = this.form.id;
      this.submitEdit(body);
    } else {
      this.submitCreate(body);
    }
  }
  submitCreate(body: ContaDTO) {
    this.submitting = true;
    this.contaService.post('/conta', body).subscribe({
      next: (response) => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_criada_com_sucesso'),
        });
        this.form = this.createEmptyForm();
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  submitEdit(body: ContaDTO) {
    this.submitting = true;
    this.contaService.put('/conta/' + body.id, body).subscribe({
      next: (response) => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_atualizada_com_sucesso'),
        });
        this.form = this.createEmptyForm();
        this.isEdit = false;
        this.idEdit = undefined;
        this.router.navigate(['/nova-conta']);
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  createEmptyForm(): ContaFormDTO {
    // Tipo Conta padrão: BANCO (campo desabilitado na tela)
    const tipoBanco = this.tipoConta?.find(t => t.value === TipoConta.BANCO);
    return {
      // codigoConta: null,
      descricao: '',
      moeda: undefined,
      numeroConta: '',
      saldo: null,
      tipoConta: tipoBanco || { label: 'Banco', value: TipoConta.BANCO },
      tituloDaConta: '',
      valorTotalDeEntradas: 0,
      valorTotalDeSaidas: 0,
      aceitaTarjetaQrcode: false,
    };
  }
}
